import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Luxury vinyl plank, 520 sq ft (468 sq ft of floor plus waste allowance)', amount: '$2,340' },
  { item: 'Install floating LVP in kitchen, dining room and hall, undercut door jambs', amount: '$1,820' },
  { item: 'Pull up and dispose of old laminate and pad; feather-fill two low spots', amount: '$690' },
  { item: 'Optional: new quarter round, painted white, 210 linear ft', amount: '$420' },
];

const faq = [
  {
    q: 'How much waste should a flooring estimate include?',
    a: 'It depends on the product, the layout and the room shapes: a straight lay in a rectangle wastes less than a diagonal lay or a herringbone. Whatever allowance you use, show it as its own quantity on the line so the customer understands why the material order is larger than the floor.',
  },
  {
    q: 'What happens if the subfloor is bad once the old floor is up?',
    a: 'Say on the quote that subfloor repairs found after removal are priced and approved before you continue. In practice that keeps a soft spot from turning into a free afternoon of work.',
  },
  {
    q: 'Can the customer add trim or stair work to the quote themselves?',
    a: 'Yes. Mark trim, transitions or stair nosing as optional lines in Scopie, and the customer ticks the ones they want on the quote page. The total recalculates for them before they sign.',
  },
  {
    q: 'Does Scopie measure rooms or work out square footage?',
    a: 'No. Scopie does not take measurements or calculate waste. You measure the job; Scopie turns your numbers into a quote, then an install date and an invoice.',
  },
];

const page: IndustryPage = {
  slug: 'flooring',
  order: 12,
  group: 'Home services',
  path: '/for/flooring',
  primaryKeyword: 'flooring contractor software',
  secondaryKeywords: ['flooring estimate template', 'flooring quote software', 'how to estimate flooring jobs'],
  title: 'Flooring Contractor Software for Install Quotes | Scopie', // 55
  metaDescription:
    'Quote flooring with material, waste, install, tear-out and subfloor prep on separate lines, offer trim as an add-on, and get a signature before you order.', // 153
  h1: 'Flooring quotes that separate material, labor and the surprises underneath',
  answerFirst:
    'Scopie lets flooring installers book the measure, then quote material with its waste allowance, installation, tear-out and subfloor prep as separate lines, with trim and transitions offered as add-ons. Because special-order product is not returnable, the deposit rule is on the quote the homeowner signs, before you place the order.',
  exampleQuote: { label: 'Sample LVP install quote', lines, total: '$5,270', depositNote: 'Material deposit and acclimation terms are the installer\'s own clauses.' },
  blocks: [
    {
      type: 'list',
      id: 'measure-to-order',
      h2: 'From the measure to the material order',
      style: 'steps',
      items: [
        { title: 'Book the measure', text: 'Log the request and put an assessment visit on your schedule, the same way you book install days.' },
        { title: 'Measure and look underneath', text: 'Room sizes, the existing floor, moisture, height changes at doorways and stairs.' },
        { title: 'Quote it in parts', text: 'Material with waste, labor by area, removal, prep, then trim and transitions as add-ons.' },
        { title: 'Order after the signature', text: 'Send the link, let the homeowner choose the extras and sign, then place the order.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A kitchen, dining room and hall in vinyl plank',
      label: 'Sample quote: 468 sq ft of LVP',
      lines,
      total: '$5,270',
      totalLabel: 'Total with new quarter round',
      note: 'Example figures. The quarter round is optional; the homeowner decides on the quote page.',
    },
    {
      type: 'text',
      id: 'big-box',
      h2: 'Competing with the big-box installed price',
      lead: 'The store said it was all-in. It was not.',
      paragraphs: [
        'Store quotes often look cheaper because removal, leveling, moving appliances and trim are extra or missing. When your quote lists each of those, the homeowner can compare like with like, and you stop losing jobs to a price that grows after the old floor comes up.',
        'If the homeowner is buying their own product, the quote shows labor only and says who is responsible for ordering enough.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Six flooring quote mistakes that cost real money',
      style: 'mistakes',
      items: [
        { title: 'Waste folded into the price', text: 'Customers think they are paying for floor they will not get. Show it.' },
        { title: 'No subfloor clause', text: 'Moisture, squeaks and soft plywood show up after demolition. Say how you handle them.' },
        { title: 'Appliances and furniture ignored', text: 'Moving the fridge and the piano is work. Price it or exclude it.' },
        { title: 'Acclimation left out', text: 'Hardwood and some plank need days on site. Put it in the timeline.' },
        { title: 'Special orders without a deposit', text: 'Non-returnable product needs your deposit rule signed first.' },
        { title: 'Transitions as an afterthought', text: 'Height changes at doorways and stairs are where callbacks come from.' },
      ],
    },
    {
      type: 'text',
      id: 'install',
      h2: 'Install week',
      paragraphs: [
        'Once signed, the install goes on the schedule with the installers and the tools they need, such as the undercut saw or the floor scraper. Installers see their addresses and start times, and the quote stays in the office. When the last transition strip is down, the invoice is a link you can share, and the homeowner stays on file for when they want the bedrooms done to match.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Flooring estimate questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/painting', anchor: 'Scopie for painting contractors', status: 'live' },
    { href: '/for/handyman', anchor: 'Scopie for handyman businesses', status: 'live' },
    { href: '/templates/flooring-estimate-template', anchor: 'free flooring estimate template', status: 'live' },
  ],
  cta: {
    heading: 'Measure today, signed quote tonight',
    text: 'Set up your products, labor rates and add-ons in Scopie and quote each floor straight after the measure.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
